import { useState } from 'react'
import { PopupModal } from 'react-calendly'

import { SendOfferEmail, completeSpecialOffer } from 'core/application/employeeSignup/legalDocuments'

import CategoryItemPageHeader from 'common/components/onBoarding/Common'

import useSharedState from 'common/hooks/useSharedState'

import { Button, Card, Col, Notification, Row } from 'common/components'
import { SharedStateKeys, useCommandRequestV2, useLoginUserV2 } from 'common/hooks'

import { BaseComponentProps } from '../models'

export type RemodelHealthShopProps = {
  numberOfQuestions: number
  currentQuestion: number
  title?: string
  questionText?: string
  productQuestion?: string
}
const RemodelHealthShop: React.FC<BaseComponentProps<RemodelHealthShopProps>> = (props) => {
  const { bindSubmitFormHandler } = props
  const [showModal, setShowModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { user, reloadUser } = useLoginUserV2()
  const [isProduction] = useSharedState<boolean>(SharedStateKeys.IsProduction)

  const { sendCommand: sendCompleteExtraStep } = useCommandRequestV2(({}) => completeSpecialOffer())
  const { sendCommand: sendOfferEmailRequest } = useCommandRequestV2(({}) => SendOfferEmail())
  const onSubmit = async () => {
    const result = await sendCompleteExtraStep({})
    if (result.completed) {
      await reloadUser()
      props.finish()
    } else {
      props.setIsSubmiting(false)
    }
  }

  bindSubmitFormHandler(onSubmit)

  const handleSendEmail = async () => {
    setIsLoading(true)
    const result = await sendOfferEmailRequest({})
    if (result.completed) {
      Notification.open({
        message: 'Email sent successfully',
        description: '',
        type: 'success',
      })
    }
    setIsLoading(false)
  }

  return (
    <div>
      <>
        <Row justify={window.innerWidth < 767 ? 'center' : 'start'} className="mt-32">
          <Col xs={24} sm={24} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
            <CategoryItemPageHeader
              title={props.state.title ?? 'Legal document'}
              numberOfQuestions={props.state.numberOfQuestions}
              currentQuestion={props.state.currentQuestion}
              question={props.state.questionText ?? 'Unlock Personalized Insurance Support with Remodel Health'}
            />
          </Col>
        </Row>

        <Row justify={window.innerWidth < 767 ? 'center' : 'start'}>
          <Col xs={24} sm={24} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
            <div className="ml-8">
              {props.state.productQuestion && (
                <Col className="mb-24 mt-16">
                  <h5 className="text-bold">{props.state.productQuestion}</h5>
                </Col>
              )}

              <p className={'font-14 mt-16 ml-4'}>
                Maximize your PeopleKeep benefit with Remodel Health. Their licensed experts offer personalized support
                that simplifies the process of choosing insurance. You’ll also have a team of advocates on your side to
                help navigate premium payments and more.
              </p>
            </div>
          </Col>
        </Row>

        <Row justify={window.innerWidth < 767 ? 'center' : 'start'} className="mt-32 mb-32">
          <Col xs={24} sm={24} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
            <Card className={'ml-8'}>
              <Row gutter={8} className={'flex'}>
                <Col span={4}>
                  <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_6867_20535)">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0 50.5516V49.7195C0 22.3352 22.1994 0.135742 49.5838 0.135742H50.4162C77.8006 0.135742 100 22.3352 100 49.7195V50.552C100 77.9363 77.8006 100.136 50.4162 100.136H49.5838C22.1994 100.136 0 77.9363 0 50.552"
                        fill="#E6FFEE"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M15.7897 51.9449C15.7497 52.377 15.6245 52.7969 15.4214 53.1803C15.2183 53.5638 14.9413 53.9032 14.6064 54.1791C14.2715 54.455 13.8852 54.6618 13.47 54.7877C13.0547 54.9135 12.6186 54.9559 12.1869 54.9124C11.7552 54.8689 11.3364 54.7403 10.9546 54.5341C10.5728 54.3279 10.2356 54.0481 9.96244 53.711C9.6893 53.3738 9.48562 52.9859 9.36315 52.5697C9.24067 52.1534 9.20183 51.717 9.24885 51.2856C9.34283 50.4236 9.77364 49.6336 10.4475 49.0877C11.1213 48.5418 11.9835 48.2843 12.8463 48.3713C13.7091 48.4583 14.5025 48.8827 15.0539 49.552C15.6052 50.2214 15.8697 51.0815 15.7897 51.9449Z"
                        fill="#8BF7B8"
                      />
                      <path
                        d="M19.213 58.1679L14.1895 62.4527M14.6859 53.3179L18.8492 55.6783L14.6859 53.3179Z"
                        stroke="#8BF7B8"
                        stroke-width="2"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M15.8381 63.2695C15.7782 63.8635 15.4849 64.4093 15.0226 64.787C14.5603 65.1647 13.9669 65.3433 13.3729 65.2834C12.779 65.2236 12.2331 64.9303 11.8554 64.468C11.4777 64.0057 11.2992 63.4123 11.359 62.8183C11.3886 62.5242 11.4759 62.2388 11.6158 61.9785C11.7557 61.7181 11.9456 61.4878 12.1745 61.3008C12.4034 61.1138 12.6669 60.9737 12.9499 60.8885C13.233 60.8034 13.53 60.7748 13.8241 60.8044C14.1182 60.834 14.4036 60.9213 14.664 61.0612C14.9244 61.2011 15.1546 61.391 15.3416 61.6199C15.5286 61.8488 15.6687 62.1123 15.7539 62.3953C15.8391 62.6784 15.8677 62.9754 15.8381 63.2695ZM22.8734 56.8729C22.8459 57.171 22.7597 57.4607 22.6197 57.7254C22.4797 57.99 22.2887 58.2243 22.0576 58.4147C21.8266 58.6051 21.5601 58.7478 21.2736 58.8347C20.9871 58.9216 20.6863 58.9508 20.3884 58.9208C20.0905 58.8908 19.8015 58.802 19.5381 58.6597C19.2747 58.5174 19.0421 58.3244 18.8538 58.0917C18.6654 57.859 18.5249 57.5913 18.4406 57.3041C18.3562 57.0168 18.3295 56.7157 18.3622 56.4181C18.4273 55.8238 18.7246 55.2793 19.1892 54.9031C19.6539 54.5269 20.2483 54.3494 20.8432 54.4094C21.438 54.4694 21.9851 54.7619 22.3653 55.2233C22.7456 55.6846 22.9282 56.2775 22.8734 56.8729ZM39.3287 10.6517L51.1728 10.6461C51.4121 10.646 51.649 10.5988 51.87 10.5072C52.0911 10.4156 52.2919 10.2813 52.461 10.112C52.6302 9.94277 52.7643 9.74186 52.8558 9.52077C52.9473 9.29967 52.9944 9.06272 52.9943 8.82344C52.9942 8.58416 52.947 8.34724 52.8554 8.12621C52.7637 7.90517 52.6294 7.70435 52.4602 7.53521C52.2909 7.36607 52.09 7.23192 51.8689 7.14042C51.6478 7.04893 51.4109 7.00187 51.1716 7.00195L39.3276 7.00712C39.0883 7.00719 38.8513 7.05441 38.6302 7.14606C38.4092 7.23771 38.2083 7.372 38.0392 7.54127C37.87 7.71054 37.7359 7.91147 37.6444 8.13259C37.5528 8.35371 37.5058 8.59069 37.5059 8.83C37.5059 9.0693 37.5532 9.30625 37.6448 9.52731C37.7365 9.74837 37.8708 9.94921 38.04 10.1184C38.2093 10.2875 38.4102 10.4217 38.6313 10.5132C38.8525 10.6047 39.0894 10.6518 39.3287 10.6517ZM43.859 17.9345L55.7031 17.9293C56.1817 17.922 56.6382 17.7268 56.974 17.3857C57.3098 17.0446 57.4979 16.5851 57.4977 16.1064C57.4975 15.6278 57.309 15.1684 56.9729 14.8277C56.6368 14.4869 56.1801 14.292 55.7015 14.2851L43.8574 14.2903C43.6182 14.2904 43.3813 14.3376 43.1602 14.4293C42.9392 14.521 42.7384 14.6553 42.5693 14.8245C42.4002 14.9938 42.266 15.1947 42.1746 15.4158C42.0831 15.6369 42.0361 15.8739 42.0362 16.1132C42.0363 16.3525 42.0835 16.5894 42.1752 16.8104C42.2668 17.0314 42.4011 17.2322 42.5704 17.4013C42.7397 17.5705 42.9406 17.7046 43.1617 17.7961C43.3828 17.8875 43.6198 17.9346 43.859 17.9345Z"
                        fill="#8BF7B8"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M44.8018 10.6459V10.6452L51.0709 10.6428C50.8294 10.6393 50.5896 10.6839 50.3654 10.7739C50.1412 10.8639 49.9372 10.9977 49.7652 11.1673C49.5932 11.3369 49.4566 11.539 49.3634 11.7618C49.2702 11.9847 49.2223 12.2239 49.2223 12.4655C49.2224 12.707 49.2705 12.9462 49.3639 13.169C49.4572 13.3918 49.5939 13.5938 49.766 13.7633C49.9382 13.9328 50.1423 14.0664 50.3665 14.1563C50.5907 14.2462 50.8306 14.2906 51.0721 14.2869L44.8033 14.2897V14.2889C45.2864 14.2887 45.7497 14.0966 46.0911 13.7549C46.4325 13.4131 46.6243 12.9497 46.624 12.4666C46.6238 11.9836 46.4317 11.5203 46.09 11.1789C45.7482 10.8374 45.2848 10.6457 44.8018 10.6459ZM73.2898 53.8624C73.0702 56.041 71.1264 57.6289 68.9482 57.4093C66.7696 57.1896 65.1817 55.2454 65.4013 53.0673C65.6206 50.8887 67.5648 49.3008 69.7434 49.5204C71.9219 49.74 73.5099 51.6839 73.2902 53.8624"
                        fill="#8BF7B8"
                      />
                      <path
                        d="M76.6596 62.1277L70.6006 67.2954M71.1999 56.2783L76.2203 59.1249L71.1999 56.2783Z"
                        stroke="#8BF7B8"
                        stroke-width="2"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M73.3466 67.5212C73.3148 67.879 73.2123 68.2269 73.045 68.5449C72.8778 68.8628 72.6491 69.1443 72.3722 69.3732C72.0954 69.6021 71.7758 69.7738 71.4321 69.8783C71.0884 69.9828 70.7274 70.018 70.37 69.9819C70.0126 69.9459 69.6659 69.8392 69.35 69.6682C69.0341 69.4972 68.7553 69.2652 68.5297 68.9856C68.3041 68.706 68.1363 68.3844 68.0359 68.0395C67.9355 67.6946 67.9046 67.3332 67.9449 66.9762C68.025 66.2664 68.3818 65.6167 68.9378 65.1681C69.4938 64.7195 70.2042 64.5081 70.915 64.5798C71.6258 64.6515 72.2797 65.0005 72.7348 65.5511C73.19 66.1018 73.4098 66.8096 73.3466 67.5212ZM80.7628 60.8755C80.7296 61.235 80.6255 61.5843 80.4565 61.9033C80.2875 62.2224 80.0571 62.5048 79.7784 62.7343C79.4998 62.9639 79.1784 63.1359 78.8329 63.2406C78.4874 63.3453 78.1246 63.3806 77.7654 63.3443C77.4062 63.3081 77.0577 63.2011 76.7401 63.0295C76.4225 62.8579 76.1419 62.6251 75.9147 62.3446C75.6875 62.064 75.5181 61.7413 75.4163 61.3949C75.3144 61.0486 75.2822 60.6855 75.3214 60.3266C75.3997 59.6095 75.7582 58.9524 76.3188 58.4984C76.8794 58.0444 77.5966 57.8303 78.3143 57.9027C79.032 57.9751 79.692 58.3282 80.1506 58.885C80.6093 59.4418 80.8293 60.1572 80.7628 60.8755Z"
                        fill="#8BF7B8"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M60.3452 76.3653C60.3452 76.5994 60.2522 76.8239 60.0867 76.9894C59.9212 77.1549 59.6967 77.2479 59.4626 77.2479C59.2286 77.2479 59.0041 77.1549 58.8386 76.9894C58.6731 76.8239 58.5801 76.5994 58.5801 76.3653C58.5801 76.1313 58.6731 75.9068 58.8386 75.7413C59.0041 75.5758 59.2286 75.4828 59.4626 75.4828C59.6967 75.4828 59.9212 75.5758 60.0867 75.7413C60.2522 75.9068 60.3452 76.1313 60.3452 76.3653V76.3653ZM89.4743 46.0728C89.4743 46.307 89.3813 46.5315 89.2158 46.697C89.0502 46.8626 88.8257 46.9556 88.5916 46.9556C88.3574 46.9556 88.1329 46.8626 87.9674 46.697C87.8018 46.5315 87.7088 46.307 87.7088 46.0728C87.7088 45.8388 87.8018 45.6143 87.9673 45.4488C88.1328 45.2833 88.3573 45.1903 88.5914 45.1903C88.8254 45.1903 89.0499 45.2833 89.2154 45.4488C89.3809 45.6143 89.4739 45.8388 89.4739 46.0728H89.4743ZM85.9433 59.9838C85.9433 60.0998 85.9204 60.2146 85.876 60.3217C85.8316 60.4288 85.7666 60.5261 85.6846 60.6081C85.6026 60.6901 85.5052 60.7551 85.3981 60.7994C85.291 60.8438 85.1761 60.8666 85.0602 60.8666C84.9442 60.8666 84.8294 60.8437 84.7223 60.7993C84.6152 60.7549 84.5179 60.6898 84.4359 60.6078C84.3539 60.5258 84.2889 60.4285 84.2446 60.3213C84.2002 60.2142 84.1774 60.0994 84.1774 59.9834C84.1774 59.7493 84.2705 59.5247 84.436 59.3591C84.6016 59.1935 84.8262 59.1005 85.0604 59.1005C85.2946 59.1005 85.5191 59.1935 85.6847 59.3591C85.8503 59.5247 85.9433 59.7493 85.9433 59.9834V59.9838ZM70.2692 42.7285C70.2717 42.846 70.2507 42.9629 70.2075 43.0721C70.1642 43.1814 70.0996 43.281 70.0174 43.3649C69.9352 43.4489 69.837 43.5156 69.7287 43.5612C69.6203 43.6067 69.504 43.6302 69.3865 43.6302C69.269 43.6302 69.1526 43.6067 69.0443 43.5612C68.9359 43.5156 68.8378 43.4489 68.7556 43.3649C68.6733 43.281 68.6087 43.1814 68.5655 43.0721C68.5222 42.9629 68.5012 42.846 68.5037 42.7285C68.5086 42.4977 68.6038 42.2779 68.7688 42.1163C68.9338 41.9548 69.1556 41.8643 69.3865 41.8643C69.6174 41.8643 69.8391 41.9548 70.0042 42.1163C70.1692 42.2779 70.2643 42.4977 70.2692 42.7285ZM77.5671 36.5837C77.5696 36.7012 77.5486 36.818 77.5054 36.9273C77.4621 37.0366 77.3975 37.1361 77.3153 37.2201C77.2331 37.3041 77.1349 37.3708 77.0266 37.4164C76.9182 37.4619 76.8019 37.4854 76.6844 37.4854C76.5668 37.4854 76.4505 37.4619 76.3421 37.4164C76.2338 37.3708 76.1357 37.3041 76.0534 37.2201C75.9712 37.1361 75.9066 37.0366 75.8634 36.9273C75.8201 36.818 75.7991 36.7012 75.8016 36.5837C75.8065 36.3528 75.9017 36.1331 76.0667 35.9715C76.2317 35.81 76.4534 35.7195 76.6844 35.7195C76.9153 35.7195 77.137 35.81 77.302 35.9715C77.467 36.1331 77.5622 36.3528 77.5671 36.5837V36.5837ZM80.2548 71.7528C80.2548 71.987 80.1618 72.2116 79.9962 72.3772C79.8307 72.5427 79.6061 72.6358 79.3719 72.6358C79.1377 72.6358 78.9132 72.5427 78.7476 72.3772C78.582 72.2116 78.489 71.987 78.489 71.7528C78.489 71.5186 78.582 71.2941 78.7476 71.1285C78.9132 70.9629 79.1377 70.8699 79.3719 70.8699C79.6061 70.8699 79.8307 70.9629 79.9962 71.1285C80.1618 71.2941 80.2548 71.5186 80.2548 71.7528V71.7528Z"
                        stroke="#8BF7B8"
                        stroke-width="2"
                      />
                      <path
                        d="M87.7079 46.5472L82.608 49.6853L76.5271 46.5468V37.4675M85.158 60.8665L84.3986 68.8832L80.0581 71.2624L85.158 60.8665ZM68.9755 69.402L66.451 73.2999H61.939L60.1484 75.8753L68.9755 69.402ZM69.4839 49.6921V43.415V49.6921Z"
                        stroke="#8BF7B8"
                        stroke-width="2"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M66.166 26.166H64.5967C64.0976 26.1646 63.6193 25.9658 63.2663 25.6128C62.9133 25.2599 62.7144 24.7817 62.7129 24.2825C62.7143 23.7833 62.9132 23.3049 63.2662 22.952C63.6192 22.599 64.0975 22.4 64.5967 22.3987H66.166C66.6652 22.4 67.1436 22.599 67.4966 22.952C67.8496 23.3049 68.0485 23.7833 68.0499 24.2825C68.0484 24.7817 67.8494 25.2599 67.4965 25.6128C67.1435 25.9658 66.6652 26.1646 66.166 26.166Z"
                        fill="#8BF7B8"
                      />
                      <path
                        d="M51.3975 48.7773C51.3975 48.7773 49.3036 51.7177 47.6553 53.0542"
                        stroke="#E6FFEE"
                        stroke-width="1.051"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M47.3858 42.6067C47.3858 42.6067 47.5654 44.3408 46.541 55.3412"
                        stroke="#E6FFEE"
                        stroke-width="1.101"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M28 40C28 36.6863 30.6863 34 34 34H69C72.3137 34 75 36.6863 75 40V61C75 64.3137 72.3137 67 69 67H67.125L61.25 74.5L55.375 67H51.5H34C30.6863 67 28 64.3137 28 61V40Z"
                        fill="#11D175"
                      />
                      <g filter="url(#filter0_d_6867_20535)">
                        <rect x="35" y="43" width="32" height="3" rx="1.5" fill="#8BF7B8" />
                      </g>
                      <g filter="url(#filter1_d_6867_20535)">
                        <rect x="35" y="54" width="23" height="3" rx="1.5" fill="#8BF7B8" />
                      </g>
                    </g>
                    <defs>
                      <filter
                        id="filter0_d_6867_20535"
                        x="31"
                        y="41"
                        width="40"
                        height="11"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                      >
                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                          in="SourceAlpha"
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                          result="hardAlpha"
                        />
                        <feOffset dy="2" />
                        <feGaussianBlur stdDeviation="2" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                          type="matrix"
                          values="0 0 0 0 0.0462485 0 0 0 0 0.580729 0 0 0 0 0.324624 0 0 0 1 0"
                        />
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_6867_20535" />
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_6867_20535" result="shape" />
                      </filter>
                      <filter
                        id="filter1_d_6867_20535"
                        x="31"
                        y="52"
                        width="31"
                        height="11"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                      >
                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                          in="SourceAlpha"
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                          result="hardAlpha"
                        />
                        <feOffset dy="2" />
                        <feGaussianBlur stdDeviation="2" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                          type="matrix"
                          values="0 0 0 0 0.0462485 0 0 0 0 0.580729 0 0 0 0 0.324624 0 0 0 1 0"
                        />
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_6867_20535" />
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_6867_20535" result="shape" />
                      </filter>
                      <clipPath id="clip0_6867_20535">
                        <rect width="100" height="100" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </Col>
                <Col span={20}>
                  <h5 className="text-bold">Get help from Remodel Health through your preferred method:</h5>
                  <p>
                    Shop for a policy via an invite link to your email or book an appointment with us to help you to
                    choose the best policy.
                  </p>
                </Col>
              </Row>
              <Row>
                <Col span={4} />
                <Col span={20}>
                  <Button type={'primary'} onClick={handleSendEmail} loading={isLoading}>
                    Email me the offer
                  </Button>
                  <Button className={'ml-8'} type={'default'} onClick={() => setShowModal(true)}>
                    Book an appointment
                  </Button>
                  <PopupModal
                    url={
                      isProduction
                        ? 'https://calendly.com/remodel-health/peoplekeep-remodel-health-partner-advisements?hide_gdpr_banner=1'
                        : 'https://calendly.com/ritech'
                    }
                    // pageSettings={props.pageSettings}
                    // utm={this.props.utm}
                    // prefill={this.props.prefill}
                    onModalClose={() => setShowModal(false)}
                    open={showModal}
                    rootElement={document.getElementById('root')}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </>
    </div>
  )
}
export default RemodelHealthShop
